/**
 * Client for `/api/portal/my-org/*` — the ORG-ADMIN surface (P4.6-C).
 *
 * Built on `api.ts`'s exported `authFetch`, so these calls carry the same
 * bearer-token header and throw the same `ApiError` as every other
 * `/api/portal/*` call. See that function's own comment for why this shares
 * the tenant plane's primitive instead of copying `adminApi.ts`'s
 * self-contained one: `orgAdmin` is a `custom:role` value on the SAME Cognito
 * app client, verified by the SAME portalAuthorizer, so there is one plane
 * here, not two.
 *
 * ============================================================================
 * EVERY TYPE BELOW MIRRORS A REAL HANDLER RESPONSE, FIELD FOR FIELD
 * ============================================================================
 * Read from DockPro-Backend's actual handlers rather than inferred from the
 * plan doc — `portalMyOrgHandler.ts`, `portalMyOrgTenantsHandler.ts`, and the
 * five mutation handlers. Four details are easy to get wrong and are called
 * out where they appear below:
 *   - org `createdAt` is an ISO STRING; tenant `createdAt`/`updatedAt` are
 *     Unix MILLISECONDS. The backend deliberately does NOT normalise them
 *     (see adminOrgView.ts's header: "so a reader can never be misled about
 *     which clock a value came from"), so neither does this.
 *   - `seats` is `null` for a `kind:"direct"` org — core ships no seat
 *     counter for it, and the backend refuses to invent one.
 *   - enterprise `extraUsers` is ALWAYS 0 with `orgUsersCounted: false`.
 *   - `tier` is the STORED value and may be null on older rows;
 *     `effectiveTier` is what the system actually applies.
 *
 * ============================================================================
 * THE 404 THAT IS NOT A BUG
 * ============================================================================
 * Every tenant-scoped mutation here returns **404 `TENANT_NOT_FOUND`** with
 * the message "No such tenant in your organization" when the tenant does not
 * exist OR exists in another org OR has no orgId at all — one
 * indistinguishable outcome, never a 403. That is a deliberate
 * anti-enumeration property, not an inconsistency: tenant ids are low-entropy
 * human-chosen slugs that appear in public embed URLs (`?builder=<tenantId>`),
 * and org admins are mutually untrusted customers who are frequently
 * competitors. A 403/404 split would confirm which slugs are real DockPro
 * customers. See orgAdminScope.ts's header. UI code must therefore NOT
 * present a 404 here as "that tenant belongs to someone else".
 */
import { authFetch, ApiError } from "./api";

// === Wire types ============================================================

/**
 * The org's contractual entitlement ceiling.
 *
 * Structurally identical to `adminApi.ts`'s own `OrgEntitlementCeiling` and
 * declared separately ON PURPOSE: this module must not depend on the
 * platform-admin client, which is a different identity plane. The shape is
 * owned by the backend/@dockpro/core, not by either client. Because
 * TypeScript is structural, `adminCeiling.ts`'s PURE value helpers
 * (`describeCeiling`, `ceilingFieldExceeded`, `formatCeilingValue`,
 * `CEILING_FIELD_LABELS`) accept this type directly — those are genuinely
 * plane-agnostic ceiling math with no runtime dependency on `adminApi.ts`
 * (their only import from it is a type-only one, which is erased), so the
 * org-admin pages reuse them rather than restating the diff logic.
 */
export interface OrgEntitlementCeiling {
  submitLimitPerMonth: number | null;
  costOutputs: boolean;
  editScope: string[] | "full";
  templatesOnly: boolean;
}

export type DerivedOrgStatus = "active" | "inactive" | "empty";

export interface OrgSeatSummary {
  model: "whiteLabel" | "enterprise";
  /** whiteLabel: billable builder tenants. enterprise: billable locations. */
  seats: number;
  /** enterprise only. Always 0 in v1 — see `orgUsersCounted`. */
  extraUsers?: number;
  /** enterprise only — the org's contracted allowance, or core's default. */
  includedUsersPerLocation?: number;
  /**
   * enterprise only, and ALWAYS false in v1. The backend cannot enumerate
   * "the users belonging to org X": org membership lives in the Cognito
   * `custom:orgId` attribute, and Cognito's ListUsers cannot filter on custom
   * attributes at all, so the only mechanism would be a full-pool scan on
   * every read. `seats` (locations) IS genuinely correct; `extraUsers` is a
   * placeholder 0. The UI MUST render this as an explicit "not counted yet"
   * rather than showing the 0 as a fact — that is exactly why the backend
   * surfaces the flag instead of hiding it. See adminOrgView.ts's header.
   */
  orgUsersCounted?: boolean;
}

export interface MyOrgSummary {
  orgId: string;
  kind: string;
  name: string | null;
  status: DerivedOrgStatus;
  /** ISO-8601 STRING on org rows — not Unix ms. See this file's header. */
  createdAt: string | null;
  entitlementCeiling: OrgEntitlementCeiling | null;
  stripeCustomerId: string | null;
  tenantCount: number;
  activeTenantCount: number;
  /** null for a `kind:"direct"` org — no seat concept exists. */
  seats: OrgSeatSummary | null;
}

/** Machine-readable reasons no self-serve billing destination exists. The UI
 * switches on these rather than on the prose in `detail`. */
export type BillingUnavailableReason =
  | "whiteLabelConnectNotImplemented"
  | "noOrgScopedBillingSurface";

export interface MyOrgBillingView {
  billedAt: string | null;
  stripeAccount: string | null;
  stripeCustomerId: string | null;
  seatBasis: string | null;
  /**
   * `available` is `false` for EVERY org kind today, and that is the whole
   * billing story for v1 rather than a gap to route around: whiteLabel has no
   * Stripe Connect integration, enterprise has no org-scoped billing surface
   * anywhere in the backend, and a direct org's billing is its single
   * tenant's own `/billing` page, not an org one. The honest unavailable
   * state IS the feature — `reason`/`detail` are what to render. There is no
   * link to build here, and no billing MUTATION exists in this round.
   */
  selfServe: {
    available: boolean;
    reason?: BillingUnavailableReason;
    detail?: string;
  };
}

export interface MyOrgResponse {
  org: MyOrgSummary;
  billing: MyOrgBillingView;
  /** `containsLeadData` is always false — P4.6-C ships no cross-tenant lead
   * content or reporting at all. Restated per-response by the backend so the
   * scope fence is visible in the payload itself. */
  scope: { orgId: string; containsLeadData: boolean };
}

/** An administrative hold as an ORG ADMIN sees it. Note what is missing
 * versus the platform-admin view: `suspendedByAdminId` is stripped (a
 * BlueCrane staff member's Cognito sub is another organization's internal
 * identifier), replaced by `heldBy`. */
export interface MyOrgAdminHold {
  suspendedAt: number | null;
  reason: string | null;
  preSuspensionStatus: string | null;
  heldBillingStatus: string | null;
  /**
   * ALWAYS the literal `"unknown"` in v1, and deliberately so. The tenant row
   * records WHO suspended it but not which KIND of admin they were — by
   * design, since an org-admin suspension writes a byte-identical row to a
   * platform-admin one so every other consumer keeps working. Distinguishing
   * the two actors lives in the CloudWatch audit line, not on this read path,
   * and guessing from the row would require enumerating the org's Cognito
   * users (impossible — see OrgSeatSummary.orgUsersCounted). So the UI must
   * not claim who imposed a hold; the timestamp and reason ARE reliable.
   */
  heldBy: "unknown";
}

export interface MyOrgTenant {
  tenantId: string;
  /** As STORED — may be null on rows predating the tier field. */
  tier: string | null;
  /** What every read path actually applies. */
  effectiveTier: string;
  designerPreset: string | null;
  status: string | null;
  /** core's own `tenantHasAccess(status)` — the same predicate `/api/config`
   * gates on AND the same one the seat counters use, so "is served" and "is
   * billable" cannot drift. Never re-derived client-side from `status`. */
  hasAccess: boolean;
  /** Unix MILLISECONDS on tenant rows. See this file's header. */
  createdAt: number | null;
  updatedAt: number | null;
  trialEndsAt: number | null;
  currentPeriodEnd: number | null;
  cancelAtPeriodEnd: boolean | null;
  entitlementOverrideKeys: string[];
  adminHold: MyOrgAdminHold | null;
}

export interface MyOrgTenantsResponse {
  orgId: string;
  orgKind: string;
  /**
   * "builder" (whiteLabel/direct) or "location" (enterprise) — the kind-aware
   * noun D-9's framing calls for, stated BY THE BACKEND rather than mapped
   * from `orgKind` in the UI. Use this verbatim; do not re-derive it.
   */
  tenantNoun: string;
  tenants: MyOrgTenant[];
  count: number;
  containsLeadData: boolean;
}

// === Reads =================================================================

export async function getMyOrg(idToken: string): Promise<MyOrgResponse> {
  return await authFetch("/api/portal/my-org", idToken) as MyOrgResponse;
}

export async function listMyOrgTenants(idToken: string): Promise<MyOrgTenantsResponse> {
  return await authFetch("/api/portal/my-org/tenants", idToken) as MyOrgTenantsResponse;
}

// === Mutations =============================================================

export interface ProvisionMyOrgTenantInput {
  tenantId: string;
  ownerEmail: string;
  businessName: string;
  ownerName?: string;
  tier?: string;
}

export interface ProvisionMyOrgTenantResult {
  tenantId: string;
  orgId: string;
  tier: string;
  status: string;
  businessName: string;
  owner: {
    loginEmail: string;
    /** Returned ONCE, over TLS, to the org admin who just created the
     * account — never emailed, and never retrievable again. */
    tempPassword: string;
    forceChangePassword: boolean;
  };
  credentialDelivery: string;
  /** The backend states the billing consequence in the response (D-9: each
   * provision = a seat = money), so the UI quotes it rather than composing
   * its own claim about invoicing. */
  seatImpact: string;
}

/**
 * POST /api/portal/my-org/tenants — provision a builder/location in the
 * caller's OWN org. The org is fixed from the authorizer context: sending an
 * `orgId` in the body is REJECTED with 400 ORG_ID_NOT_ACCEPTED rather than
 * ignored, so this deliberately has no orgId parameter to pass.
 *
 * `entitlementOverrides` is likewise refused (400 OVERRIDES_NOT_ACCEPTED) —
 * a per-tenant override is a hand-configured deal exception, BlueCrane's
 * instrument and not a customer's — which is why this input accepts only a
 * `tier`.
 *
 * Rejections a caller must be ready for: 422
 * ENTITLEMENTS_EXCEED_ORG_CEILING (tier above the org's contractual
 * ceiling — body carries `candidate`/`orgCeiling`), 422
 * PROVISION_NOT_ALLOWED_FOR_DIRECT_ORG, 409 TENANT_ID_TAKEN, 409
 * OWNER_EMAIL_EXISTS.
 */
export async function provisionMyOrgTenant(
  idToken: string,
  input: ProvisionMyOrgTenantInput,
): Promise<ProvisionMyOrgTenantResult> {
  return await authFetch("/api/portal/my-org/tenants", idToken, {
    method: "POST",
    body: JSON.stringify(input),
  }) as ProvisionMyOrgTenantResult;
}

export interface SuspendMyOrgTenantResult {
  tenantId: string;
  status: string;
  preSuspensionStatus: string;
  suspendedAt: number;
  reason: string | null;
  seatImpact: string;
}

/**
 * POST .../{tenantId}/suspend. `reason` is optional (a bare suspend is
 * valid); when present it must be non-empty and <= 500 chars, so an
 * all-whitespace box is sent as no reason at all rather than as a 400.
 */
export async function suspendMyOrgTenant(
  idToken: string,
  tenantId: string,
  reason?: string,
): Promise<SuspendMyOrgTenantResult> {
  const trimmed = reason?.trim();
  return await authFetch(
    `/api/portal/my-org/tenants/${encodeURIComponent(tenantId)}/suspend`,
    idToken,
    { method: "POST", body: JSON.stringify(trimmed ? { reason: trimmed } : {}) },
  ) as SuspendMyOrgTenantResult;
}

export interface ReactivateMyOrgTenantResult {
  tenantId: string;
  status: string;
  /** Which source the restored status came from — the backend's own
   * resolution order is heldBillingStatus -> preSuspensionStatus ->
   * "active". Surfaced so an operator can see WHY it landed where it did. */
  restoredFrom: string;
  reactivatedAt: number;
  seatImpact: string;
}

/**
 * POST .../{tenantId}/reactivate — lifts an administrative hold ONLY.
 * A tenant that is not under a hold is a 409 NOT_SUSPENDED, not a no-op
 * success: flipping a billing-denied tenant to active here would be a
 * billing mutation dressed up as an admin action.
 */
export async function reactivateMyOrgTenant(
  idToken: string,
  tenantId: string,
): Promise<ReactivateMyOrgTenantResult> {
  return await authFetch(
    `/api/portal/my-org/tenants/${encodeURIComponent(tenantId)}/reactivate`,
    idToken,
    { method: "POST" },
  ) as ReactivateMyOrgTenantResult;
}

export interface UpdateMyOrgTenantTierInput {
  tier?: string;
  /** `null` explicitly CLEARS the named preset; omitting the key leaves it
   * unchanged. Note the backend does NOT retroactively strip the entitlement
   * overrides a cleared preset had applied (they are indistinguishable from
   * BlueCrane-set ones), and says so in a `note` on the response. */
  designerPreset?: string | null;
}

export interface UpdateMyOrgTenantTierResult {
  tenantId: string;
  orgId: string;
  tier: string;
  designerPreset: string | null;
  entitlementOverrides: Record<string, unknown> | null;
  withinOrgCeiling: boolean;
  note?: string;
}

/**
 * PUT .../{tenantId}/tier — assign a tier and/or designer preset, bounded by
 * the org's ceiling.
 *
 * Two INDEPENDENT invariants are enforced server-side and either can reject:
 *   - 422 ENTITLEMENTS_EXCEED_ORG_CEILING — the merged candidate grant
 *     exceeds the org's contractual ceiling (body carries `candidate` and
 *     `orgCeiling`).
 *   - 422 PRESET_NOT_ALLOWED_FOR_TIER — the preset grants more than the
 *     PROPOSED tier allows (checked against the proposed tier, not the
 *     stored one).
 * "Restrict-only" means bounded BY THE CEILING, never "downgrade-only": a
 * tenant may freely move UP to and including the org's ceiling.
 */
export async function updateMyOrgTenantTier(
  idToken: string,
  tenantId: string,
  input: UpdateMyOrgTenantTierInput,
): Promise<UpdateMyOrgTenantTierResult> {
  return await authFetch(
    `/api/portal/my-org/tenants/${encodeURIComponent(tenantId)}/tier`,
    idToken,
    { method: "PUT", body: JSON.stringify(input) },
  ) as UpdateMyOrgTenantTierResult;
}

export interface ResendMyOrgCredentialsResult {
  tenantId: string;
  loginEmail: string;
  passwordRotated: boolean;
  emailSent: boolean;
  forceChangePassword: boolean;
  emailVerified: boolean;
  /** Present when the owner's email is unverified — fresh credentials alone
   * won't admit them, because the email-verification gate still blocks
   * login. */
  warning?: string;
}

/**
 * POST .../{tenantId}/resend-credentials — rotate the owner's temporary
 * password and email it. Takes NO email parameter by design: accepting one
 * would turn this into "mail a working credential for tenant X to an address
 * of my choosing".
 *
 * THE FAILURE MODE THAT MATTERS: a 502 CREDENTIAL_EMAIL_FAILED means the
 * password WAS rotated but the email did NOT send — the owner's old password
 * is dead and nobody has told them the new one. That is not a plain failure
 * and must never be shown as one; see `credentialsRotatedDespiteFailure`.
 */
export async function resendMyOrgCredentials(
  idToken: string,
  tenantId: string,
): Promise<ResendMyOrgCredentialsResult> {
  return await authFetch(
    `/api/portal/my-org/tenants/${encodeURIComponent(tenantId)}/resend-credentials`,
    idToken,
    { method: "POST" },
  ) as ResendMyOrgCredentialsResult;
}

// === Error shaping =========================================================

export const CEILING_REJECTION_CODE = "ENTITLEMENTS_EXCEED_ORG_CEILING";

export interface CeilingRejection {
  message: string;
  candidate: OrgEntitlementCeiling;
  orgCeiling: OrgEntitlementCeiling;
}

/**
 * Pull the ceiling-rejection detail out of a 422 so the UI can show WHICH
 * component exceeded the ceiling instead of just "no".
 *
 * Returns null unless the error really is a ceiling rejection carrying BOTH
 * objects — a 422 whose body lacked them would otherwise render an empty diff
 * table that looks like a bug.
 */
export function ceilingRejectionFrom(err: unknown): CeilingRejection | null {
  if (!(err instanceof ApiError)) return null;
  if (err.status !== 422 || err.code !== CEILING_REJECTION_CODE) return null;
  const candidate = err.body?.candidate;
  const orgCeiling = err.body?.orgCeiling;
  if (!candidate || typeof candidate !== "object") return null;
  if (!orgCeiling || typeof orgCeiling !== "object") return null;
  return {
    message: err.message,
    candidate: candidate as OrgEntitlementCeiling,
    orgCeiling: orgCeiling as OrgEntitlementCeiling,
  };
}

/**
 * True when a failed resend-credentials call nonetheless ROTATED the
 * password. Such an operator has a real problem to act on (deliver the new
 * password out of band, or retry) and must not be left thinking nothing
 * happened.
 */
export function credentialsRotatedDespiteFailure(err: unknown): boolean {
  return err instanceof ApiError && err.body?.passwordRotated === true;
}

/** The 404 every out-of-scope OR missing tenant collapses into. Exported so
 * the UI can recognise it without spelling the string twice, and so a test
 * can assert the exact wording the backend guarantees. */
export const TENANT_NOT_IN_ORG_CODE = "TENANT_NOT_FOUND";
export const TENANT_NOT_IN_ORG_MESSAGE = "No such tenant in your organization";

/**
 * The tiers an org admin may choose. Mirrors the backend's own
 * `isValidTenantTier` ("basic, mid, top"). The org's ceiling further bounds
 * which of these will actually be ACCEPTED, and the server is the real gate —
 * this list only stops the UI offering a value that is invalid outright.
 */
export const ALL_TENANT_TIERS = ["basic", "mid", "top"] as const;

/**
 * Mirrors the backend's `TENANT_ID_RX` exactly (3-50 chars, lowercase
 * alphanumerics and hyphens, must start and end alphanumeric). Bounds come
 * from Cognito's own `custom:tenantId` attribute limits, so a value outside
 * them would fail at AdminCreateUser time — i.e. AFTER the handler had begun
 * writing. Client-side mirroring is for a good error experience only.
 */
export const TENANT_ID_RULE =
  "3-50 characters: lowercase letters, digits and hyphens, starting and ending " +
  "with a letter or digit";

export function isValidTenantId(value: string): boolean {
  return /^[a-z0-9](?:[a-z0-9-]{1,48})[a-z0-9]$/.test(value);
}

/** Deliberately permissive, matching the backend's own EMAIL_RX posture: this
 * catches a typo, it does not adjudicate address validity. */
export function looksLikeEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}
